import React from 'react';
import { cn } from '../lib/utils';
import { User } from 'lucide-react';

interface AvatarProps {
  src?: string | null;
  alt?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  fallback?: React.ReactNode;
  loading?: boolean;
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ src, alt = 'User avatar', size = 'md', className, fallback, loading = false }, ref) => {
    const [error, setError] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);

    const sizeClasses = {
      xs: 'w-6 h-6',
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-16 h-16',
      xl: 'w-24 h-24',
    };

    const handleError = () => {
      setError(true);
      setLoaded(true);
    };

    const handleLoad = () => {
      setLoaded(true);
    };

    return (
      <div
        ref={ref}
        className={cn(
          'relative rounded-full overflow-hidden bg-surface flex items-center justify-center',
          sizeClasses[size],
          className
        )}
      >
        {/* Loading state */}
        {loading && !loaded && (
          <div className="absolute inset-0 bg-surface animate-pulse" />
        )}

        {/* Fallback or error state */}
        {(error || !src) && (
          <div className="absolute inset-0 bg-neutral flex items-center justify-center">
            {fallback || (
              <User
                className={cn(
                  'text-surface-light',
                  size === 'xs' && 'w-3 h-3',
                  size === 'sm' && 'w-4 h-4',
                  size === 'md' && 'w-5 h-5',
                  size === 'lg' && 'w-8 h-8',
                  size === 'xl' && 'w-12 h-12'
                )}
              />
            )}
          </div>
        )}

        {/* Actual image */}
        {src && !error && (
          <img
            src={src}
            alt={alt}
            referrerPolicy="no-referrer"  
            className={cn(
              'w-full h-full object-cover',
              !loaded && 'invisible'
            )}
            loading="lazy"
            onError={handleError}
            onLoad={handleLoad}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

export default Avatar;